/**
 * IntelProfilePanel — Perfilador Criminal integrado.
 * 4 pestañas de búsqueda: Persona · Teléfono · Compañía · IP/Dominio
 * Vista Resumen + toggle Vista Analista (OsintDashboard)
 */
import { useState, useRef, useEffect } from "react";
import axios from "axios";
import OsintDashboard from "./OsintDashboard";

const API = process.env.REACT_APP_API_URL || "http://localhost:8001/api/v1";

const NIVEL = {
  CRITICO: { bg: "#fef2f2", borde: "#ef4444", badge: "#dc2626", texto: "#991b1b", icon: "🔴" },
  ALTO:    { bg: "#fff7ed", borde: "#f97316", badge: "#ea580c", texto: "#9a3412", icon: "🟠" },
  MEDIO:   { bg: "#fefce8", borde: "#eab308", badge: "#ca8a04", texto: "#854d0e", icon: "🟡" },
  BAJO:    { bg: "#f0fdf4", borde: "#22c55e", badge: "#16a34a", texto: "#166534", icon: "🟢" },
};

// ── Normalizador de teléfono ─────────────────────────────────────────────────

function normalizarTelefono(raw) {
  const solo_digitos = raw.replace(/\D/g, "");
  // Quitar prefijo 52 si es número mexicano de 12 dígitos
  if (solo_digitos.startsWith("52") && solo_digitos.length === 12) {
    return solo_digitos.slice(2);
  }
  if (solo_digitos.startsWith("1") && solo_digitos.length === 11) {
    return solo_digitos.slice(1);
  }
  return solo_digitos;
}

function variantes_telefono(raw) {
  const base = normalizarTelefono(raw);
  if (base.length < 8) return [base];
  const variantes = new Set();
  variantes.add(base);
  variantes.add(`+52${base}`);
  variantes.add(`+52 ${base.slice(0, 2)} ${base.slice(2, 6)} ${base.slice(6)}`);
  variantes.add(`${base.slice(0, 4)} ${base.slice(4)}`);
  variantes.add(`${base.slice(0, 2)} ${base.slice(2, 6)} ${base.slice(6)}`);
  return [...variantes];
}

// ── Componentes auxiliares ───────────────────────────────────────────────────

function RiesgoBadge({ nivel }) {
  const c = NIVEL[nivel] || NIVEL.BAJO;
  return (
    <span style={{
      display: "inline-block",
      background: c.badge, color: "#fff",
      fontWeight: 800, fontSize: 13, letterSpacing: 1,
      padding: "4px 16px", borderRadius: 99,
    }}>
      {nivel}
    </span>
  );
}

function ScoreBar({ score }) {
  const color = score >= 70 ? "#dc2626" : score >= 45 ? "#ea580c" : score >= 20 ? "#ca8a04" : "#16a34a";
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 4 }}>
        <span style={{ fontSize: 36, fontWeight: 800, color, lineHeight: 1 }}>{score}</span>
        <span style={{ color: "#6b7280", fontSize: 14 }}>/100 score de riesgo</span>
      </div>
      <div style={{ background: "#e5e7eb", borderRadius: 99, height: 10 }}>
        <div style={{ background: color, height: 10, borderRadius: 99, width: score + "%", transition: "width 0.6s ease" }} />
      </div>
    </div>
  );
}

function Seccion({ titulo, icono, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ borderTop: "1px solid #e5e7eb", marginTop: 0 }}>
      <button
        onClick={() => setOpen(v => !v)}
        style={{
          width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center",
          padding: "14px 24px", background: "none", border: "none", cursor: "pointer",
          textAlign: "left",
        }}
      >
        <span style={{ fontWeight: 700, fontSize: 14, color: "#111827" }}>
          {icono} {titulo}
        </span>
        <span style={{ color: "#9ca3af", fontSize: 18 }}>{open ? "▾" : "▸"}</span>
      </button>
      {open && <div style={{ padding: "0 24px 20px" }}>{children}</div>}
    </div>
  );
}

function NoticiaCard({ art }) {
  const fecha = art.fecha ? art.fecha.substring(0, 10) : "";
  return (
    <div style={{ display: "flex", gap: 12, padding: "10px 0", borderBottom: "1px solid #f3f4f6" }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <a
          href={art.url} target="_blank" rel="noreferrer"
          style={{ fontWeight: 600, fontSize: 14, color: "#1d4ed8", textDecoration: "none", display: "block" }}
        >
          {art.titulo || "(sin título)"}
        </a>
        <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
          {art.fuente && <span style={{ marginRight: 8 }}>{art.fuente}</span>}
          {fecha && <span>{fecha}</span>}
        </div>
        {art.descripcion && (
          <p style={{ fontSize: 13, color: "#374151", marginTop: 4, lineHeight: 1.5 }}>
            {art.descripcion.substring(0, 180)}{art.descripcion.length > 180 ? "…" : ""}
          </p>
        )}
      </div>
    </div>
  );
}

function SancionesTable({ ofac, opensanctions }) {
  const hits = ofac?.coincidencias || [];
  const osHits = opensanctions?.resultados || [];
  if (!ofac?.en_lista_ofac && !osHits.length) {
    return <p style={{ color: "#6b7280", fontSize: 14 }}>Sin coincidencias en listas de sanciones.</p>;
  }
  return (
    <div>
      {ofac?.en_lista_ofac && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: "#dc2626", marginBottom: 8 }}>
            ⚠️ OFAC SDN — Lista de Sanciones del Tesoro de EE.UU.
          </div>
          <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#fef2f2" }}>
                <th style={{ textAlign: "left", padding: "6px 8px", color: "#374151" }}>Nombre</th>
                <th style={{ textAlign: "left", padding: "6px 8px", color: "#374151" }}>Tipo</th>
                <th style={{ textAlign: "left", padding: "6px 8px", color: "#374151" }}>Programa</th>
              </tr>
            </thead>
            <tbody>
              {hits.map((h, i) => (
                <tr key={i} style={{ borderTop: "1px solid #fee2e2" }}>
                  <td style={{ padding: "6px 8px" }}>{h.nombre}</td>
                  <td style={{ padding: "6px 8px" }}>{h.tipo}</td>
                  <td style={{ padding: "6px 8px" }}>{h.programa}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {osHits.length > 0 && (
        <div>
          <div style={{ fontWeight: 700, fontSize: 13, color: "#9a3412", marginBottom: 8 }}>
            🌐 OpenSanctions — Bases de datos internacionales
          </div>
          {osHits.slice(0, 5).map((r, i) => (
            <div key={i} style={{
              padding: "8px 12px", background: "#fff7ed",
              borderRadius: 6, marginBottom: 6, fontSize: 13,
            }}>
              <strong>{r.nombre}</strong> — {r.schema}
              {r.datasets?.length > 0 && (
                <div style={{ color: "#6b7280", fontSize: 12, marginTop: 2 }}>
                  Fuentes: {r.datasets.slice(0, 3).join(", ")}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function DorksCompacto({ dorks }) {
  const categorias = [...new Set(dorks.map(d => d.categoria))];
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {categorias.map(cat => (
        <div key={cat}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5 }}>
            {cat}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {dorks.filter(d => d.categoria === cat).map((d, i) => (
              <a
                key={i} href={d.url} target="_blank" rel="noreferrer" title={d.dork}
                style={{
                  fontSize: 12, background: "#f0f9ff", color: "#0369a1",
                  border: "1px solid #bae6fd", padding: "4px 10px",
                  borderRadius: 20, textDecoration: "none",
                }}
              >
                {d.descripcion}
              </a>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function SpiderFootLanzador({ termino }) {
  const [sfDisponible, setSfDisponible] = useState(null);
  const [estado, setEstado] = useState("idle");
  const [scanId, setScanId] = useState(null);
  const [sfUrl, setSfUrl] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(API + "/spiderfoot/status", { withCredentials: true })
      .then(r => setSfDisponible(r.data.disponible === true))
      .catch(() => setSfDisponible(false));
  }, []);

  async function lanzar() {
    setEstado("lanzando");
    setError(null);
    try {
      const res = await axios.post(
        API + "/spiderfoot/scan/iniciar",
        { objetivo: termino, tipo_objetivo: "HUMAN_NAME" },
        { withCredentials: true }
      );
      if (res.data?.ok) {
        setScanId(res.data.scan_id);
        setSfUrl(res.data.url_sf);
        setEstado("corriendo");
      } else {
        setError(res.data?.error || "Error al iniciar");
        setEstado("error");
      }
    } catch (e) {
      setError(e.response?.data?.detail || "SpiderFoot no disponible");
      setEstado("error");
    }
  }

  if (sfDisponible === null) return (
    <p style={{ color: "#9ca3af", fontSize: 13 }}>Verificando disponibilidad...</p>
  );

  if (!sfDisponible) return (
    <div style={{ color: "#6b7280", fontSize: 13, fontStyle: "italic" }}>
      SpiderFoot no está disponible en este servidor. El módulo requiere instalación local.
    </div>
  );

  if (estado === "idle") return (
    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
      <button onClick={lanzar} style={{ background: "#1e40af", color: "#fff", border: "none", padding: "8px 18px", borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>
        🕷️ Iniciar escaneo SpiderFoot
      </button>
      <span style={{ color: "#6b7280", fontSize: 12 }}>Escaneo profundo — 500+ sitios, redes sociales, brechas de datos</span>
    </div>
  );
  if (estado === "lanzando") return <p style={{ color: "#6b7280", fontSize: 14 }}>Iniciando escaneo…</p>;
  if (estado === "corriendo") return (
    <div style={{ background: "#eff6ff", borderRadius: 8, padding: 12 }}>
      <div style={{ fontWeight: 600, color: "#1d4ed8", fontSize: 14, marginBottom: 4 }}>✅ Escaneo iniciado — ID: {scanId}</div>
      <p style={{ fontSize: 13, color: "#374151", marginBottom: 8 }}>El escaneo corre en background. Puede tomar varios minutos.</p>
      <a href={sfUrl} target="_blank" rel="noreferrer" style={{ display: "inline-block", background: "#1d4ed8", color: "#fff", padding: "6px 14px", borderRadius: 6, fontSize: 13, textDecoration: "none", fontWeight: 600 }}>
        Ver resultados en SpiderFoot →
      </a>
    </div>
  );
  return (
    <div style={{ color: "#dc2626", fontSize: 13 }}>
      ⚠️ {error}
      <button onClick={() => setEstado("idle")} style={{ marginLeft: 12, fontSize: 12, color: "#6b7280", background: "none", border: "none", cursor: "pointer" }}>Reintentar</button>
    </div>
  );
}

// ── Sección harvester ────────────────────────────────────────────────────────

function CopyChip({ text, href, color, bg, border, mono }) {
  const [copied, setCopied] = useState(false);
  const copy = (e) => {
    e.preventDefault();
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  const chipStyle = {
    fontSize: 12, background: bg, color: copied ? "#16a34a" : color,
    border: `1px solid ${copied ? "#86efac" : border}`,
    padding: "3px 10px", borderRadius: 20, cursor: "pointer",
    fontFamily: mono ? "monospace" : "inherit",
    textDecoration: "none", display: "inline-block",
    transition: "color .2s, border-color .2s",
    userSelect: "none",
  };
  if (href) return (
    <span style={{ display: "inline-flex", gap: 2, alignItems: "center" }}>
      <a href={href} target="_blank" rel="noreferrer" style={chipStyle}>{text}</a>
      <span title="Copiar" onClick={copy}
        style={{ fontSize: 11, cursor: "pointer", color: copied ? "#16a34a" : "#9ca3af",
                 padding: "2px 4px", borderRadius: 6 }}>
        {copied ? "✓" : "⎘"}
      </span>
    </span>
  );
  return (
    <span onClick={copy} style={chipStyle} title="Clic para copiar">
      {copied ? "✓ copiado" : text}
    </span>
  );
}

function HarvesterResultados({ harvester }) {
  if (!harvester?.disponible) return null;
  if (!harvester.total_hallazgos) return (
    <div style={{ color: "#6b7280", fontSize: 13 }}>Sin hallazgos de infraestructura para este objetivo.</div>
  );
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {harvester.emails?.length > 0 && (
        <div>
          <div style={{ fontWeight: 700, fontSize: 12, color: "#374151", marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5 }}>
            Emails encontrados ({harvester.emails.length})
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {harvester.emails.map((e, i) => (
              <CopyChip key={i} text={e} href={`mailto:${e}`}
                color="#166534" bg="#f0fdf4" border="#bbf7d0" />
            ))}
          </div>
        </div>
      )}
      {harvester.hosts?.length > 0 && (
        <div>
          <div style={{ fontWeight: 700, fontSize: 12, color: "#374151", marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5 }}>
            Hosts / Subdominios ({harvester.hosts.length})
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {harvester.hosts.slice(0, 30).map((h, i) => (
              <CopyChip key={i} text={h} href={`https://${h}`}
                color="#1d4ed8" bg="#eff6ff" border="#bfdbfe" />
            ))}
            {harvester.hosts.length > 30 && (
              <span style={{ fontSize: 12, color: "#6b7280", alignSelf: "center" }}>
                +{harvester.hosts.length - 30} más
              </span>
            )}
          </div>
        </div>
      )}
      {harvester.ips?.length > 0 && (
        <div>
          <div style={{ fontWeight: 700, fontSize: 12, color: "#374151", marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5 }}>
            IPs asociadas ({harvester.ips.length})
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {harvester.ips.map((ip, i) => (
              <CopyChip key={i} text={ip}
                href={`https://www.shodan.io/host/${ip}`}
                color="#7c3aed" bg="#faf5ff" border="#ddd6fe" mono />
            ))}
          </div>
        </div>
      )}
      {harvester.shodan?.length > 0 && (
        <div>
          <div style={{ fontWeight: 700, fontSize: 12, color: "#374151", marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5 }}>
            Shodan — Infraestructura expuesta ({harvester.shodan.length})
          </div>
          {harvester.shodan.map((s, i) => (
            <div key={i} style={{ fontSize: 12, background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 6, padding: "8px 12px", marginBottom: 4 }}>
              {s.ip && <span style={{ fontFamily: "monospace", fontWeight: 700, color: "#c2410c" }}>{s.ip}</span>}
              {s.ports?.length > 0 && (
                <span style={{ marginLeft: 10, color: "#6b7280" }}>
                  Puertos: {s.ports.map(p => (
                    <span key={p} style={{ display: "inline-block", background: "#fed7aa", color: "#92400e", borderRadius: 4, padding: "0 5px", marginLeft: 3, fontFamily: "monospace", fontSize: 11 }}>{p}</span>
                  ))}
                </span>
              )}
              {s.org && <span style={{ marginLeft: 10, color: "#78716c", fontSize: 11 }}>{s.org}</span>}
              {s.country && <span style={{ marginLeft: 8, fontSize: 11, color: "#a8a29e" }}>({s.country})</span>}
              {!s.ip && <span style={{ fontFamily: "monospace", color: "#92400e" }}>{JSON.stringify(s)}</span>}
            </div>
          ))}
        </div>
      )}
      <div style={{ fontSize: 11, color: "#9ca3af" }}>
        Fuentes: {harvester.fuentes_usadas?.join(", ")} · theHarvester 4.10.1
      </div>
    </div>
  );
}

// ── Formularios por tipo de búsqueda ─────────────────────────────────────────

const TABS = [
  { id: "persona",   label: "👤 Persona",    tipo: "persona"  },
  { id: "telefono",  label: "📞 Teléfono",   tipo: "telefono" },
  { id: "empresa",   label: "🏢 Compañía",   tipo: "empresa"  },
  { id: "ip",        label: "🌐 IP / Dominio", tipo: "ip"     },
];

function SatValidador({ rfc }) {
  const [estado, setEstado] = useState("idle"); // idle | cargando | ok | error
  const [resultado, setResultado] = useState(null);
  const [error, setError] = useState(null);

  async function validar() {
    const r = rfc.trim().toUpperCase();
    if (!r) return;
    setEstado("cargando"); setError(null); setResultado(null);
    try {
      const { data } = await axios.get(`${API}/gobierno/sat/completo/${encodeURIComponent(r)}`);
      setResultado(data);
      setEstado("ok");
    } catch (e) {
      setError(e.response?.data?.detail || "Error al consultar SAT");
      setEstado("error");
    }
  }

  const riesgoColor = { ALTO: "#dc2626", BAJO: "#16a34a", DESCONOCIDO: "#6b7280" };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, flex: 1, minWidth: 160 }}>
      <button onClick={validar} disabled={!rfc.trim() || estado === "cargando"}
        style={{
          border: "1.5px solid #d1d5db", borderRadius: 8, padding: "9px 12px", fontSize: 12, fontWeight: 700,
          background: rfc.trim() ? "#111827" : "#f3f4f6", color: rfc.trim() ? "#fff" : "#9ca3af",
          cursor: rfc.trim() ? "pointer" : "not-allowed",
        }}>
        {estado === "cargando" ? "⏳ Consultando…" : "🏛️ Validar RFC en SAT"}
      </button>
      {estado === "error" && (
        <span style={{ fontSize: 11, color: "#dc2626" }}>❌ {error}</span>
      )}
      {estado === "ok" && resultado && (
        <div style={{ fontSize: 11, border: "1px solid #e5e7eb", borderRadius: 8, padding: "8px 10px", background: "#fafafa" }}>
          <div style={{ marginBottom: 4 }}>
            {resultado.validacion.valido ? "✅" : "❌"} {resultado.validacion.mensaje}
            {resultado.validacion.tipo && <span style={{ color: "#6b7280" }}> · {resultado.validacion.tipo === "PERSONA_MORAL" ? "Persona moral" : "Persona física"}</span>}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{
              fontWeight: 800, fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5,
              color: "#fff", background: riesgoColor[resultado.riesgo_general] || "#6b7280",
              borderRadius: 4, padding: "2px 8px",
            }}>
              Riesgo {resultado.riesgo_general}
            </span>
            <span style={{ color: "#6b7280" }}>{resultado.lista_69b.mensaje}</span>
          </div>
        </div>
      )}
    </div>
  );
}

function FormPersona({ onBuscar, cargando }) {
  const [ap, setAp] = useState("");
  const [am, setAm] = useState("");
  const [n1, setN1] = useState("");
  const [n2, setN2] = useState("");
  const [alias, setAlias] = useState("");
  const [rfc, setRfc] = useState("");
  const [curp, setCurp] = useState("");

  function armar() {
    const nombre = [n1, n2].filter(Boolean).join(" ").trim();
    const apellidos = [ap, am].filter(Boolean).join(" ").trim();
    const termino = [nombre, apellidos].filter(Boolean).join(" ").trim();
    if (!termino) return;
    onBuscar({ termino, alias: alias.trim() || null, tipo: "persona",
      extra: { campos: { nombre_1: n1.trim(), nombre_2: n2.trim(),
                         apellido_paterno: ap.trim(), apellido_materno: am.trim(),
                         rfc: rfc.trim(), curp: curp.trim() } } });
  }

  const F = (label, val, set, placeholder = "") => (
    <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1, minWidth: 120 }}>
      <label style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</label>
      <input value={val} onChange={e => set(e.target.value)} onKeyDown={e => e.key === "Enter" && armar()} placeholder={placeholder}
        style={{ border: "1.5px solid #d1d5db", borderRadius: 8, padding: "9px 12px", fontSize: 13, outline: "none" }}
        onFocus={e => e.target.style.borderColor = "#111827"} onBlur={e => e.target.style.borderColor = "#d1d5db"} />
    </div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        {F("Apellido Paterno", ap, setAp, "García")}
        {F("Apellido Materno", am, setAm, "López")}
        {F("Primer Nombre", n1, setN1, "Juan")}
        {F("Segundo Nombre", n2, setN2, "Carlos")}
      </div>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        {F("Alias / Apodo", alias, setAlias, "El Tigre")}
        {F("RFC", rfc, setRfc, "GALJ800101XXX")}
        {F("CURP", curp, setCurp, "GALJ800101HDFRNN09")}
        <SatValidador rfc={rfc} />
      </div>
      <BtnBuscar onClick={armar} disabled={cargando || (!n1 && !ap)} cargando={cargando} />
    </div>
  );
}

function FormTelefono({ onBuscar, cargando }) {
  const [tel, setTel] = useState("");
  const preview = tel.trim() ? variantes_telefono(tel.trim()) : [];

  function armar() {
    const t = tel.trim();
    if (!t) return;
    const normalizado = normalizarTelefono(t);
    onBuscar({ termino: normalizado, alias: null, tipo: "telefono", extra: { variantes: variantes_telefono(t) } });
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", gap: 10, alignItems: "flex-end" }}>
        <div style={{ flex: 1 }}>
          <label style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.5, display: "block", marginBottom: 4 }}>Número de teléfono</label>
          <input
            value={tel} onChange={e => setTel(e.target.value)} onKeyDown={e => e.key === "Enter" && armar()}
            placeholder="+52 55 1234 5678  ó  5512345678  ó  +525512345678"
            style={{ width: "100%", border: "1.5px solid #d1d5db", borderRadius: 8, padding: "9px 12px", fontSize: 13, outline: "none", boxSizing: "border-box" }}
            onFocus={e => e.target.style.borderColor = "#111827"} onBlur={e => e.target.style.borderColor = "#d1d5db"}
          />
        </div>
        <BtnBuscar onClick={armar} disabled={cargando || !tel.trim()} cargando={cargando} />
      </div>
      {preview.length > 1 && (
        <div style={{ fontSize: 12, color: "#6b7280" }}>
          <span style={{ fontWeight: 600 }}>Variantes a buscar: </span>
          {preview.map((v, i) => (
            <span key={i} style={{ background: "#f3f4f6", border: "1px solid #e5e7eb", borderRadius: 4, padding: "2px 8px", marginRight: 6, fontFamily: "monospace", fontSize: 11 }}>{v}</span>
          ))}
        </div>
      )}
    </div>
  );
}

function FormEmpresa({ onBuscar, cargando }) {
  const [nombre, setNombre] = useState("");
  const [rfc, setRfc] = useState("");
  const [ubicacion, setUbicacion] = useState("");
  const [ramo, setRamo] = useState("");
  const [dominio, setDominio] = useState("");

  function armar() {
    if (!nombre.trim()) return;
    onBuscar({ termino: nombre.trim(), alias: dominio.trim() || null, tipo: "empresa", extra: { rfc: rfc.trim(), ubicacion: ubicacion.trim(), ramo: ramo.trim(), dominio: dominio.trim() } });
  }

  const F = (label, val, set, placeholder = "") => (
    <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1, minWidth: 120 }}>
      <label style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</label>
      <input value={val} onChange={e => set(e.target.value)} onKeyDown={e => e.key === "Enter" && armar()} placeholder={placeholder}
        style={{ border: "1.5px solid #d1d5db", borderRadius: 8, padding: "9px 12px", fontSize: 13, outline: "none" }}
        onFocus={e => e.target.style.borderColor = "#111827"} onBlur={e => e.target.style.borderColor = "#d1d5db"} />
    </div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        {F("Nombre de la Compañía *", nombre, setNombre, "Grupo Industrial XYZ")}
        {F("RFC", rfc, setRfc, "GIX800101XXX")}
        <SatValidador rfc={rfc} />
      </div>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        {F("Ubicación", ubicacion, setUbicacion, "CDMX, Jalisco…")}
        {F("Ramo", ramo, setRamo, "Construcción, Transporte…")}
        {F("Dominio web", dominio, setDominio, "ejemplo.com")}
      </div>
      <BtnBuscar onClick={armar} disabled={cargando || !nombre.trim()} cargando={cargando} />
    </div>
  );
}

const RE_CIDR = /^\d{1,3}(\.\d{1,3}){3}\/\d{1,2}$/;
const RE_IP   = /^\d{1,3}(\.\d{1,3}){3}$/;

function FormIP({ onBuscar, cargando }) {
  const [objetivo, setObjetivo] = useState("");

  const t       = objetivo.trim().toLowerCase();
  const es_cidr = RE_CIDR.test(t);
  const es_ip   = RE_IP.test(t);

  function armar() {
    if (!t) return;
    if (es_cidr) {
      onBuscar({ termino: t, alias: null, tipo: "cidr", extra: {} });
    } else {
      onBuscar({ termino: t, alias: null, tipo: "ip", extra: { es_ip, es_dominio: !es_ip } });
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ display: "flex", gap: 10, alignItems: "flex-end" }}>
        <div style={{ flex: 1 }}>
          <label style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.5, display: "block", marginBottom: 4 }}>
            Dirección IP, Dominio o Rango CIDR
          </label>
          <input
            value={objetivo} onChange={e => setObjetivo(e.target.value)} onKeyDown={e => e.key === "Enter" && armar()}
            placeholder="192.168.1.1  ·  ejemplo.com  ·  10.0.0.0/24"
            style={{
              width: "100%", padding: "9px 12px", fontSize: 13, outline: "none",
              boxSizing: "border-box", borderRadius: 8,
              border: es_cidr ? "1.5px solid #7c3aed" : "1.5px solid #d1d5db",
            }}
            onFocus={e => e.target.style.borderColor = es_cidr ? "#7c3aed" : "#111827"}
            onBlur={e  => e.target.style.borderColor = es_cidr ? "#7c3aed" : "#d1d5db"}
          />
        </div>
        <BtnBuscar onClick={armar} disabled={cargando || !t} cargando={cargando}
          label={es_cidr ? "Escanear CIDR" : "Analizar"} />
      </div>
      {es_cidr && (
        <div style={{ fontSize: 11, color: "#7c3aed", background: "#f5f3ff", border: "1px solid #ddd6fe", borderRadius: 6, padding: "5px 10px" }}>
          🔍 Modo CIDR detectado — se escaneará el rango completo (máx. /24). Costo: 3 tokens OSINT.
        </div>
      )}
    </div>
  );
}

function BtnBuscar({ onClick, disabled, cargando, label = "Analizar" }) {
  return (
    <button
      onClick={onClick} disabled={disabled}
      style={{
        background: disabled ? "#6b7280" : "#111827",
        color: "#fff", border: "none", borderRadius: 8,
        padding: "10px 28px", fontSize: 14, fontWeight: 700,
        cursor: disabled ? "default" : "pointer", whiteSpace: "nowrap", height: 40,
      }}
    >
      {cargando ? "Consultando…" : label}
    </button>
  );
}

// ── Componente CIDR Resultados ───────────────────────────────────────────────

function CIDRResultados({ resultado }) {
  const { cidr, red_info, fuentes_usadas, total_ips_activas, ips_activas, error } = resultado;

  return (
    <div style={{ fontFamily: "system-ui, sans-serif" }}>

      {/* Resumen */}
      <div style={{
        background: "#fff", borderRadius: 12, padding: "20px 24px",
        boxShadow: "0 1px 4px rgba(0,0,0,.08)", marginBottom: 16,
        borderLeft: "5px solid #7c3aed",
      }}>
        <div style={{ fontSize: 20, fontWeight: 800, color: "#111827", marginBottom: 4 }}>
          🔭 Reconocimiento CIDR: <span style={{ color: "#7c3aed", fontFamily: "monospace" }}>{cidr}</span>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 20, marginTop: 12 }}>
          {[
            ["IPs posibles en rango", red_info?.total_hosts_posibles ?? "—"],
            ["IPs activas encontradas", total_ips_activas],
            ["Prefijo de red", `/${red_info?.prefixlen ?? "?"}`],
            ["Dirección de red", red_info?.red ?? "—"],
          ].map(([lbl, val]) => (
            <div key={lbl} style={{ textAlign: "center", minWidth: 110 }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: "#7c3aed" }}>{val}</div>
              <div style={{ fontSize: 11, color: "#9ca3af" }}>{lbl}</div>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 10, display: "flex", gap: 6, flexWrap: "wrap" }}>
          {(fuentes_usadas || []).map(f => (
            <span key={f} style={{ background: "#f5f3ff", color: "#7c3aed", border: "1px solid #ddd6fe", fontSize: 11, fontWeight: 700, padding: "2px 10px", borderRadius: 99 }}>{f}</span>
          ))}
        </div>
        {error && (
          <div style={{ marginTop: 10, fontSize: 12, color: "#d97706", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 6, padding: "6px 10px" }}>
            ⚠️ {error}
          </div>
        )}
      </div>

      {/* Tabla de IPs activas */}
      <div style={{ background: "#fff", borderRadius: 12, boxShadow: "0 1px 4px rgba(0,0,0,.08)", overflow: "hidden" }}>
        <div style={{ padding: "14px 20px", borderBottom: "1px solid #e5e7eb", fontWeight: 700, fontSize: 14, color: "#111827", background: "#f8fafc" }}>
          🖥️ IPs Activas ({total_ips_activas})
        </div>

        {!ips_activas?.length ? (
          <div style={{ padding: "40px 24px", textAlign: "center", color: "#9ca3af", fontSize: 14 }}>
            Sin IPs activas detectadas en el rango. Añade SHODAN_API_KEY para mejores resultados.
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#f8fafc" }}>
                  {["IP", "Puertos abiertos", "Servicios", "Hosts / PTR", "País / Org", "Vulns"].map(h => (
                    <th key={h} style={{ padding: "10px 14px", textAlign: "left", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: "#6b7280", borderBottom: "1px solid #e5e7eb" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ips_activas.map((ip_data, i) => (
                  <tr key={ip_data.ip} style={{ borderBottom: "1px solid #f3f4f6", background: i % 2 === 0 ? "#fff" : "#f9fafb" }}>
                    <td style={{ padding: "10px 14px", fontFamily: "monospace", fontWeight: 700, color: "#7c3aed", whiteSpace: "nowrap" }}>
                      {ip_data.ip}
                    </td>
                    <td style={{ padding: "10px 14px" }}>
                      {ip_data.puertos?.length > 0 ? (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                          {ip_data.puertos.map(p => (
                            <span key={p} style={{ background: "#eff6ff", color: "#1d4ed8", fontSize: 11, fontWeight: 700, padding: "1px 7px", borderRadius: 4, fontFamily: "monospace" }}>{p}</span>
                          ))}
                        </div>
                      ) : <span style={{ color: "#d1d5db" }}>—</span>}
                    </td>
                    <td style={{ padding: "10px 14px", fontSize: 12, color: "#374151" }}>
                      {ip_data.servicios?.filter(Boolean).join(", ") || <span style={{ color: "#d1d5db" }}>—</span>}
                    </td>
                    <td style={{ padding: "10px 14px" }}>
                      {ip_data.hosts?.length > 0 ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                          {ip_data.hosts.slice(0, 3).map((h, j) => (
                            <span key={j} style={{ fontSize: 11, color: "#0369a1", fontFamily: "monospace" }}>{h}</span>
                          ))}
                          {ip_data.hosts.length > 3 && <span style={{ fontSize: 10, color: "#9ca3af" }}>+{ip_data.hosts.length - 3} más</span>}
                        </div>
                      ) : <span style={{ color: "#d1d5db" }}>—</span>}
                    </td>
                    <td style={{ padding: "10px 14px", fontSize: 11, color: "#6b7280" }}>
                      {[ip_data.pais, ip_data.org].filter(Boolean).join(" · ") || <span style={{ color: "#d1d5db" }}>—</span>}
                    </td>
                    <td style={{ padding: "10px 14px" }}>
                      {ip_data.vulns?.length > 0 ? (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
                          {ip_data.vulns.slice(0, 3).map(v => (
                            <span key={v} style={{ background: "#fef2f2", color: "#dc2626", fontSize: 10, fontWeight: 700, padding: "1px 6px", borderRadius: 4 }}>{v}</span>
                          ))}
                        </div>
                      ) : <span style={{ color: "#d1d5db" }}>—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Panel principal ──────────────────────────────────────────────────────────

export default function IntelProfilePanel({ contextoCDR, buscarInicial, tipoInicial, aliasInicial }) {
  const [tabActiva, setTabActiva]         = useState(tipoInicial === "ip" ? "ip" : tipoInicial === "empresa" ? "empresa" : tipoInicial === "persona" ? "persona" : "telefono");
  const [cargando, setCargando]           = useState(false);
  const [perfil, setPerfil]               = useState(null);
  const [resultadoCIDR, setResultadoCIDR] = useState(null);
  const [error, setError]                 = useState(null);
  const [vistaAnalista, setVista]         = useState(false);
  const [ultimoTermino, setUltimoTermino] = useState("");
  const [ultimoTipo, setUltimoTipo]       = useState("persona");
  const [ultimoCampos, setUltimoCampos]   = useState({});
  const [guardandoCM, setGuardandoCM]     = useState(false);

  async function buscar({ termino, alias, tipo, extra }) {
    setUltimoTermino(termino);
    setUltimoTipo(tipo);
    setUltimoCampos(extra?.campos || {});
    setCargando(true);
    setError(null);
    setPerfil(null);
    setResultadoCIDR(null);
    setVista(false);

    // ── Rama CIDR: llama directamente al endpoint de harvester ──────────────
    if (tipo === "cidr") {
      try {
        const res = await axios.post(
          API + "/harvester/cidr",
          { cidr: termino, max_ips: 50 },
          { withCredentials: true }
        );
        setResultadoCIDR(res.data);
      } catch (e) {
        if (e.response?.status === 401) {
          setError("Sesión expirada. Recarga la página.");
        } else if (e.response?.status === 402) {
          setError(e.response.data?.detail || "Tokens OSINT insuficientes (se requieren 3).");
        } else if (e.response?.status === 422) {
          setError(e.response.data?.detail || "CIDR inválido. Ejemplo: 192.168.1.0/24");
        } else {
          setError(e.response?.data?.detail || "Error al escanear el rango CIDR.");
        }
      } finally {
        setCargando(false);
      }
      return;
    }

    // ── Rama normal: perfil intel ────────────────────────────────────────────
    try {
      const res = await axios.post(
        API + "/busqueda/intel-profile",
        {
          termino,
          alias: alias || null,
          tipo,
          contexto_cdr: contextoCDR || null,
          extra: extra || null,
        },
        { withCredentials: true }
      );
      setPerfil(res.data);
    } catch (e) {
      if (e.response?.status === 401) {
        setError("Sesión expirada. Recarga la página.");
      } else if (e.response?.status === 402) {
        setError(e.response.data?.detail || "Tokens OSINT insuficientes.");
      } else {
        setError(e.response?.data?.detail || "Error al consultar fuentes de inteligencia.");
      }
    } finally {
      setCargando(false);
    }
  }

  // Auto-búsqueda al llegar desde CDR/CM con ?buscar=NOMBRE&tipo=persona&alias=ALIAS
  useEffect(() => {
    if (!buscarInicial) return;
    const tipo = tipoInicial || "telefono";
    const es_ip = /^\d{1,3}(\.\d{1,3}){3}$/.test(buscarInicial);
    buscar({
      termino: buscarInicial,
      alias:   aliasInicial || null,
      tipo,
      extra:   tipo === "ip" ? { es_ip, es_dominio: !es_ip } : {},
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buscarInicial]);

  const nivel   = perfil?.nivel_riesgo || "BAJO";
  const colores = NIVEL[nivel] || NIVEL.BAJO;
  const articulos = perfil?.fuentes?.articulos || [];

  return (
    <div style={{ fontFamily: "system-ui, sans-serif" }}>

      {/* Barra de búsqueda */}
      <div style={{ background: "#fff", borderRadius: 12, padding: 24, boxShadow: "0 1px 4px rgba(0,0,0,.08)", marginBottom: 20 }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: "#111827", marginBottom: 2 }}>
          🔍 Perfilador Criminal Integrado
        </div>
        <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 16 }}>
          OFAC · OpenSanctions · GDELT · NewsAPI · Wikipedia · DuckDuckGo · Google CSE · RSS · SpiderFoot · theHarvester · Motor IA
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", gap: 4, marginBottom: 20, borderBottom: "2px solid #e5e7eb", paddingBottom: 0 }}>
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => { setTabActiva(tab.id); setPerfil(null); setResultadoCIDR(null); setError(null); setVista(false); }}
              style={{
                padding: "8px 16px", border: "none", background: "none",
                borderBottom: tabActiva === tab.id ? "2px solid #111827" : "2px solid transparent",
                marginBottom: "-2px", cursor: "pointer",
                fontWeight: tabActiva === tab.id ? 700 : 500,
                color: tabActiva === tab.id ? "#111827" : "#6b7280",
                fontSize: 13, transition: "all 0.15s",
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Formulario activo */}
        {tabActiva === "persona"  && <FormPersona  onBuscar={buscar} cargando={cargando} />}
        {tabActiva === "telefono" && <FormTelefono onBuscar={buscar} cargando={cargando} />}
        {tabActiva === "empresa"  && <FormEmpresa  onBuscar={buscar} cargando={cargando} />}
        {tabActiva === "ip"       && <FormIP       onBuscar={buscar} cargando={cargando} />}

        {cargando && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#6b7280", fontSize: 13, marginTop: 12 }}>
            <span>⏳</span>
            <span>
              {tabActiva === "ip" && RE_CIDR.test(document.activeElement?.value || "")
                ? "Escaneando rango CIDR — Shodan + reverse lookup…"
                : tabActiva === "empresa" || tabActiva === "ip"
                  ? "Consultando OFAC, sanciones internacionales, medios, fuentes de inteligencia y theHarvester…"
                  : "Consultando OFAC, sanciones internacionales, medios, fuentes de inteligencia…"
              }
            </span>
          </div>
        )}
      </div>

      {error && (
        <div style={{ background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 10, padding: 16, color: "#dc2626", fontSize: 14, marginBottom: 16 }}>
          ⚠️ {error}
        </div>
      )}

      {/* ── Resultado CIDR ── */}
      {resultadoCIDR && <CIDRResultados resultado={resultadoCIDR} />}

      {/* Toggle vista + botón Case Manager */}
      {perfil && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, gap: 8, flexWrap: "wrap" }}>
          <button
            disabled={guardandoCM}
            onClick={async () => {
              setGuardandoCM(true);
              try {
                const tipo = ultimoTipo === "telefono" ? "telefono"
                           : ultimoTipo === "empresa"  ? "empresa"
                           : "persona";
                const [resRef, resToken] = await Promise.all([
                  axios.post(
                    API + "/busqueda/guardar-temporal",
                    { termino: ultimoTermino, tipo, perfil: perfil || {}, campos: ultimoCampos },
                    { withCredentials: true }
                  ),
                  axios.get(API + "/auth/sso-externo", { withCredentials: true }),
                ]);
                const ref_id = resRef.data.ref_id;
                const sso_token = resToken.data.token;
                const cm_url = process.env.REACT_APP_CM_URL || "http://localhost:8002";
                const next = `/dashboard?osint_ref=${ref_id}&osint_termino=${encodeURIComponent(ultimoTermino)}&osint_tipo=${tipo}`;
                window.open(
                  `${cm_url}/sso?token=${encodeURIComponent(sso_token)}&next=${encodeURIComponent(next)}`,
                  "_blank"
                );
              } catch (e) {
                alert("Error al exportar al Case Manager");
              } finally {
                setGuardandoCM(false);
              }
            }}
            style={{
              padding: "6px 16px", borderRadius: 8, fontSize: 13, fontWeight: 600,
              cursor: guardandoCM ? "not-allowed" : "pointer",
              background: guardandoCM ? "#e5e7eb" : "#ede9fe",
              color: guardandoCM ? "#9ca3af" : "#4f46e5",
              border: "1px solid #c4b5fd",
            }}
          >
            {guardandoCM ? "⏳ Guardando…" : "⚖️ Agregar al expediente CM"}
          </button>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              onClick={() => setVista(false)}
              style={{
                padding: "6px 16px", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer",
                background: !vistaAnalista ? "#111827" : "#f3f4f6",
                color: !vistaAnalista ? "#fff" : "#374151",
                border: "1px solid #e5e7eb",
              }}
            >
              Vista Resumen
            </button>
            <button
              onClick={() => setVista(true)}
              style={{
                padding: "6px 16px", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer",
                background: vistaAnalista ? "#111827" : "#f3f4f6",
                color: vistaAnalista ? "#fff" : "#374151",
                border: "1px solid #e5e7eb",
              }}
            >
              📊 Vista Analista
            </button>
          </div>
        </div>
      )}

      {/* ── Vista Analista ── */}
      {perfil && vistaAnalista && (
        <OsintDashboard perfil={perfil} />
      )}

      {/* ── Vista Resumen ── */}
      {perfil && !vistaAnalista && (
        <div style={{ background: "#fff", borderRadius: 12, boxShadow: "0 1px 4px rgba(0,0,0,.08)", overflow: "hidden" }}>

          {/* Cabecera de riesgo */}
          <div style={{ background: colores.bg, borderBottom: `3px solid ${colores.borde}`, padding: "20px 24px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16 }}>
              <div>
                <div style={{ fontSize: 22, fontWeight: 800, color: "#111827", marginBottom: 4 }}>
                  {perfil.termino}
                  {perfil.alias && <span style={{ fontSize: 15, fontWeight: 500, color: "#6b7280", marginLeft: 10 }}>"{perfil.alias}"</span>}
                  {perfil.desde_cache && (
                    <span title={`Resultado en caché — expira ${perfil.cache_expira ? new Date(perfil.cache_expira).toLocaleDateString("es-MX") : "pronto"}`}
                      style={{ marginLeft: 10, fontSize: 11, fontWeight: 700, background: "#e0f2fe", color: "#0369a1",
                               border: "1px solid #7dd3fc", borderRadius: 6, padding: "2px 8px", verticalAlign: "middle", cursor: "default" }}>
                      ⚡ DESDE CACHÉ
                    </span>
                  )}
                </div>
                <RiesgoBadge nivel={nivel} />
              </div>
              <ScoreBar score={perfil.score_riesgo} />
            </div>
          </div>

          {/* Wikipedia */}
          {perfil.fuentes?.wikipedia?.encontrado && (
            <div style={{ padding: "16px 24px", background: "#f0f9ff", borderBottom: "1px solid #e0f2fe" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: "#0369a1", marginBottom: 6 }}>
                    📖 Wikipedia — {perfil.fuentes.wikipedia.titulo}
                    {perfil.fuentes.wikipedia.es_criminal && (
                      <span style={{ marginLeft: 8, background: "#dc2626", color: "#fff", fontSize: 11, padding: "1px 8px", borderRadius: 99, fontWeight: 700 }}>
                        CRIMEN ORGANIZADO
                      </span>
                    )}
                  </div>
                  <p style={{ fontSize: 13, color: "#374151", lineHeight: 1.6, margin: 0 }}>{perfil.fuentes.wikipedia.extracto}</p>
                </div>
                <a href={perfil.fuentes.wikipedia.url} target="_blank" rel="noreferrer"
                  style={{ fontSize: 12, color: "#0369a1", background: "#e0f2fe", padding: "4px 12px", borderRadius: 6, textDecoration: "none", whiteSpace: "nowrap", flexShrink: 0 }}>
                  Ver artículo →
                </a>
              </div>
            </div>
          )}

          {/* DuckDuckGo resumen */}
          {perfil.fuentes?.ddg?.encontrado && perfil.fuentes.ddg.abstract && (
            <div style={{ padding: "14px 24px", background: "#fafaf9", borderBottom: "1px solid #e7e5e4" }}>
              <div style={{ fontWeight: 700, fontSize: 12, color: "#57534e", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>
                🌐 Presencia pública confirmada
              </div>
              <p style={{ fontSize: 13, color: "#374151", lineHeight: 1.6, margin: 0 }}>{perfil.fuentes.ddg.abstract}</p>
              {perfil.fuentes.ddg.abstract_url && (
                <a href={perfil.fuentes.ddg.abstract_url} target="_blank" rel="noreferrer"
                  style={{ fontSize: 12, color: "#6b7280", marginTop: 4, display: "inline-block" }}>
                  Fuente: {perfil.fuentes.ddg.abstract_fuente}
                </a>
              )}
            </div>
          )}

          {/* Alertas */}
          {(perfil.alertas?.length > 0 || perfil.alertas_cdr?.length > 0) && (
            <Seccion titulo="Alertas detectadas" icono="⚡" defaultOpen={true}>
              {[...(perfil.alertas || []), ...(perfil.alertas_cdr || [])].map((a, i) => {
                const c = NIVEL[a.nivel] || NIVEL.BAJO;
                return (
                  <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "10px 14px", background: c.bg, borderRadius: 8, borderLeft: `4px solid ${c.badge}`, marginBottom: 8 }}>
                    <span style={{ fontSize: 16 }}>{c.icon}</span>
                    <div>
                      <span style={{ fontWeight: 700, fontSize: 12, color: c.texto }}>[{a.fuente}]</span>
                      <span style={{ fontSize: 13, color: "#374151", marginLeft: 8 }}>{a.mensaje}</span>
                    </div>
                  </div>
                );
              })}
            </Seccion>
          )}

          {/* Análisis IA */}
          {perfil.reporte_ia && (
            <Seccion titulo={`Análisis IA${perfil.reporte_ia?.dual_ia ? " — Análisis Dual" : ""}`} icono="🤖" defaultOpen={true}>
              <div style={{ marginBottom: perfil.reporte_ia?.analisis_gemini ? 16 : 0 }}>
                {perfil.reporte_ia?.dual_ia && (
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#7c3aed", textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 6 }}>Motor IA — Análisis Principal</div>
                )}
                <div style={{ background: "#f8fafc", borderRadius: 8, padding: 16, fontSize: 14, color: "#374151", lineHeight: 1.7, borderLeft: "3px solid #7c3aed" }}>
                  {typeof perfil.reporte_ia === "string" ? perfil.reporte_ia : perfil.reporte_ia.analisis_ia || perfil.reporte_ia.resumen || JSON.stringify(perfil.reporte_ia)}
                </div>
              </div>
              {perfil.reporte_ia?.analisis_gemini && (
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#0369a1", textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 6 }}>Gemini — Google AI</div>
                  <div style={{ background: "#f0f9ff", borderRadius: 8, padding: 16, fontSize: 14, color: "#374151", lineHeight: 1.7, borderLeft: "3px solid #0369a1" }}>
                    {perfil.reporte_ia.analisis_gemini}
                  </div>
                </div>
              )}
              <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 10, textAlign: "right" }}>{perfil.reporte_ia?.fuente_ia || "IA"}</div>
            </Seccion>
          )}

          {/* Sanciones */}
          <Seccion titulo={`Sanciones internacionales${perfil.fuentes?.ofac?.en_lista_ofac ? " ⚠️" : ""}`} icono="🏛️" defaultOpen={true}>
            <SancionesTable ofac={perfil.fuentes?.ofac} opensanctions={perfil.fuentes?.opensanctions} />
          </Seccion>

          {/* Resultados web DDG */}
          {perfil.fuentes?.ddg?.articulos_web?.length > 0 && (
            <Seccion titulo={`Resultados web (${perfil.fuentes.ddg.articulos_web.length} fuentes)`} icono="🌐" defaultOpen={true}>
              {perfil.fuentes.ddg.es_criminal && (
                <div style={{ background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 8, padding: "8px 14px", marginBottom: 12, fontSize: 13, color: "#991b1b", fontWeight: 600 }}>
                  ⚠️ Indicadores de crimen organizado: {(perfil.fuentes.ddg.keywords_crimen || []).slice(0, 6).join(", ")}
                </div>
              )}
              {perfil.fuentes.ddg.articulos_web.map((art, i) => <NoticiaCard key={i} art={art} />)}
            </Seccion>
          )}

          {/* Google CSE */}
          {perfil.fuentes?.google?.articulos?.length > 0 && (
            <Seccion titulo={`Google Search (${perfil.fuentes.google.articulos.length} resultados reales)`} icono="🔵" defaultOpen={true}>
              <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 10, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span>Query: <code style={{ background: "#f3f4f6", padding: "1px 6px", borderRadius: 4 }}>{perfil.fuentes.google.query_usada}</code></span>
                {perfil.fuentes.google.total && <span>~{perfil.fuentes.google.total} resultados en Google</span>}
              </div>
              {perfil.fuentes.google.articulos.map((art, i) => <NoticiaCard key={i} art={art} />)}
            </Seccion>
          )}

          {/* Medios */}
          {articulos.length > 0 && (
            <Seccion titulo={`Cobertura en medios (${articulos.length} — RSS · NewsAPI · GDELT)`} icono="📰" defaultOpen={false}>
              {articulos.slice(0, 12).map((art, i) => <NoticiaCard key={i} art={art} />)}
              {articulos.length > 12 && <p style={{ fontSize: 13, color: "#6b7280", marginTop: 8 }}>+ {articulos.length - 12} artículos adicionales</p>}
            </Seccion>
          )}

          {/* theHarvester */}
          {perfil.herramientas_externas?.harvester?.disponible && (
            <Seccion titulo={`theHarvester — Infraestructura (${perfil.herramientas_externas.harvester.total_hallazgos || 0} hallazgos)`} icono="🔭" defaultOpen={true}>
              <HarvesterResultados harvester={perfil.herramientas_externas.harvester} />
            </Seccion>
          )}

          {/* Dorks */}
          {perfil.dorks?.length > 0 && (
            <Seccion titulo="Búsquedas de investigación" icono="🔎" defaultOpen={false}>
              <p style={{ fontSize: 13, color: "#6b7280", marginBottom: 12 }}>Consultas preconfiguradas en motores de búsqueda e inteligencia pública.</p>
              <DorksCompacto dorks={perfil.dorks} />
            </Seccion>
          )}

          {/* SpiderFoot */}
          <Seccion titulo="Escaneo profundo SpiderFoot" icono="🕷️" defaultOpen={false}>
            <SpiderFootLanzador termino={perfil.termino} />
          </Seccion>

          {/* Entidades relacionadas */}
          {perfil.entidades_relacionadas?.length > 0 && (
            <Seccion titulo="Entidades relacionadas" icono="🔗" defaultOpen={false}>
              {perfil.entidades_relacionadas.map((e, i) => (
                <div key={i} style={{ fontSize: 13, padding: "6px 10px", background: "#f9fafb", borderRadius: 6, marginBottom: 4 }}>
                  <strong>{e.nombre}</strong>
                  <span style={{ color: "#6b7280", marginLeft: 8 }}>{e.tipo} · {e.fuente}</span>
                </div>
              ))}
            </Seccion>
          )}

        </div>
      )}
    </div>
  );
}
